<template>
    <div
        :class="[
            {
                'bookmark-item--not-draggable': disabled,
            },
            'bookmark-item',
        ]"
    >
        <div
            data-handle="true"
            class="bookmark-item__target"
        >
            <GripHorizontal />
        </div>
        <div v-if="bookmarkIcon" class="bookmark-item__component-icon" >
            <Component :is="bookmarkIcon" />
        </div>
        <div
            class="bookmark-item__content"
            @click="emit('click:bookmark')"
        >
            {{ bookmarkName }}
        </div>
        <div
            class="bookmark-item__delete"
            @click="emit('click:delete')"
        >
            <Trash />
        </div>
    </div>
</template>

<script setup lang="ts">
import { BookmarkDto } from '@/api/generated/HCA'
import { GripHorizontal, Trash } from 'lucide-vue-next'
import {
    flattenTransformersHierarchy,
    useTransformersHierarchyQuery,
} from '@/api/queries/transformers/useTransformersHierarchyQuery'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { flattenGeneratorsHierarchy, useGeneratorsHierarchyQuery } from '@/api/queries/generators/useGeneratorsHierarchyQuery'
import { ComponentType, ComponentTypesMap } from '@/configuration/component/component-types'

const props = defineProps<{
    bookmark: BookmarkDto
    disabled?: boolean
}>()

const emit = defineEmits<{
    'click:bookmark': []
    'click:delete': []
}>()

const { t } = useI18n()
const { data: transformersHierarchyData } = useTransformersHierarchyQuery()
const { data: generatorsHierarchyData } = useGeneratorsHierarchyQuery()

const flattenedTransformersHierarchy = computed(() => flattenTransformersHierarchy(transformersHierarchyData.value?.plantGroups ?? []))
const flattenedGeneratorsHierarchy = computed(() => flattenGeneratorsHierarchy(generatorsHierarchyData.value?.plantGroups ?? []))

const bookmarkIcon = computed(() => {
    if (!props.bookmark.componentType) {
        return
    }

    return ComponentTypesMap[props.bookmark.componentType as ComponentType]?.lucideIcon
})

const bookmarkName = computed(() => {
    if (!props.bookmark.componentSid) {
        return '-'
    }

    const transformerRecord = props.bookmark.componentType === ComponentType.Transformer ? flattenedTransformersHierarchy.value.find((e) => e.transformer.id === props.bookmark.componentSid) : undefined
    const generatorRecord = props.bookmark.componentType === ComponentType.Generator ? flattenedGeneratorsHierarchy.value.find((e) => e.generator.id === props.bookmark.componentSid) : undefined

    if (!transformerRecord && !generatorRecord) {
        return props.bookmark.componentSid
    }

    let componentType = props.bookmark.componentType === ComponentType.Transformer ? t('common.transformer') : t('common.generator')
    let plantGroup = transformerRecord?.plantGroup ?? generatorRecord?.plantGroup
    let plant = transformerRecord?.plant ?? generatorRecord?.plant
    let componentName = transformerRecord?.transformer.transformerName ?? generatorRecord?.generator.generatorName
    let componentInfo = transformerRecord?.transformer.id ?? generatorRecord?.generator.functionalLocation

    return `${componentType} / ${plantGroup?.name} / ${plant?.name} / ${props.bookmark.componentName ?? componentName} / ${componentInfo}`
})
</script>

<style scoped lang="scss">
.bookmark-item {
    margin-right: 0.5em;
    background-color: $pbl-background;
    border-radius: 4px;

    display: flex;
    gap: $spacing-xs;
    align-items: center;

    &:hover {
        .bookmark-item__delete {
            opacity: 1;
            pointer-events: auto;
        }
    }

    &--not-draggable {
        cursor: not-allowed !important;
        opacity: 0.8;

        & > * {
            cursor: not-allowed !important;
        }
    }

    &__target {
        height: 100%;
        display: flex;
        align-items: center;

        background-color: rgb(var(--pbl-border-muted) / 0.3);
        border-radius: 4px;

        padding: $spacing-s $spacing-2xs;
        cursor: move;

        & > * {
            margin-top: 2px;
        }
    }

    &__component-icon {
        color: $pbl-text-muted;
    }

    &__content {
        flex: 1;

        user-select: none;
        cursor: pointer;

        font-weight: 500;

        &:hover {
            color: $pbl-primary;
        }
    }

    &__delete {
        opacity: 0;
        pointer-events: none;
        
        display: flex;
        align-items: center;
        
        height: 100%;
        padding: $spacing-xs $spacing-2xs;
        border-radius: 4px;

        background-color: $pbl-invalid;
        cursor: pointer;

        transition: opacity 0.15s ease, background-color 0.15s ease;

        &:hover:not(&:active) {
            background-color: $pbl-invalid-contrast;
        }

        & > * {
            height: 20px;
            width: 20px;

            color: white;
        }
    }
}
</style>
